'use client';

import { memo, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  alpha,
} from '@mui/material';
import { Route as RouteIcon } from '@mui/icons-material';
import BulkBookingRow from './BulkBookingRow';
import BulkBookingRowMobile from './BulkBookingRowMobile';
import { useTranslation } from '@/kit/useTranslation';

const BUSINESS_COLORS = {
  primary: '#1a1a1a',
  accent: '#f59e0b',
};

const HEADER_STYLE = {
  fontWeight: 700,
  fontSize: '0.7rem',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  py: 0.75,
  px: 1.5,
  borderBottom: '2px solid #e2e8f0',
  bgcolor: '#f8fafc',
};

// Colors for different cluster routes
const CLUSTER_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

function ClusterHeaderRow({ clusterId, itemCount, isMultiStop, routeSavingsPercent, colSpan = 8 }) {
  const color = CLUSTER_COLORS[(clusterId - 1) % CLUSTER_COLORS.length];
  return (
    <TableRow>
      <TableCell
        colSpan={colSpan}
        sx={{
          py: 0.5,
          px: 1.5,
          bgcolor: alpha(color, 0.06),
          borderLeft: `3px solid ${color}`,
          borderBottom: `1px solid ${alpha(color, 0.15)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RouteIcon sx={{ fontSize: 14, color }} />
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color, letterSpacing: 0.5 }}>
            ROUTE {clusterId}
          </Typography>
          <Chip
            label={
              isMultiStop
                ? `${itemCount} stop${itemCount !== 1 ? 's' : ''} · shared route`
                : '1 stop · solo'
            }
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              bgcolor: alpha(color, 0.12),
              color,
              border: `1px solid ${alpha(color, 0.25)}`,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {isMultiStop && routeSavingsPercent > 0 && (
            <Chip
              label={`saves ~${Math.round(routeSavingsPercent)}% on distance`}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.6rem',
                fontWeight: 700,
                bgcolor: alpha('#10b981', 0.12),
                color: '#10b981',
                border: `1px solid ${alpha('#10b981', 0.25)}`,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}

function BulkBookingTable({
  items = [],
  primaryPickup,
  onUpdateItem,
  onDeleteItem,
  onDuplicateItem,
  isMobile,
  bulkOrder,
  showBulkBadge = true,
}) {
  const { t } = useTranslation();

  // Determine if we should show cluster grouping (only when pricing has been calculated
  // and at least one item has a cluster assignment)
  const hasClusterData = useMemo(() => items.some((it) => it.clusterId != null), [items]);

  // Group items by clusterId for cluster header rows (only used in desktop table)
  const groupedItems = useMemo(() => {
    if (!hasClusterData) return null;

    // Build ordered list of clusterIds (preserving first-seen order)
    const clusterOrder = [];
    const clusterMap = {};
    items.forEach((item) => {
      const key = item.clusterId ?? 'ungrouped';
      if (!clusterMap[key]) {
        clusterMap[key] = [];
        clusterOrder.push(key);
      }
      clusterMap[key].push(item);
    });
    return { clusterOrder, clusterMap };
  }, [items, hasClusterData]);

  // Empty state
  if (items.length === 0) {
    return (
      <Box
        sx={{
          p: 6,
          textAlign: 'center',
          bgcolor: alpha(BUSINESS_COLORS.accent, 0.03),
        }}
      >
        <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 600, mb: 1 }}>
          {t('bulkBooking.noItemsAdded')}
        </Typography>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.875rem' }}>
          {t('bulkBooking.clickAddItemsToStart')}
        </Typography>
      </Box>
    );
  }

  // Mobile: Card-based layout (with cluster grouping when pricing has been calculated)
  if (isMobile) {
    if (hasClusterData && groupedItems) {
      // Grouped mobile layout
      let globalIndex = 0;
      return (
        <Box sx={{ p: 2 }}>
          {groupedItems.clusterOrder.map((clusterKey) => {
            const clusterItems = groupedItems.clusterMap[clusterKey];
            const isMultiStopCluster =
              clusterKey !== 'ungrouped' && clusterItems.some((it) => it.isMultiStop);
            const clusterColor = CLUSTER_COLORS[(clusterKey - 1) % CLUSTER_COLORS.length];
            return (
              <Box key={`mobile-cluster-${clusterKey}`}>
                {clusterKey !== 'ungrouped' && (
                  <Box
                    sx={{
                      mb: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      bgcolor: alpha(clusterColor, 0.08),
                      border: `1px solid ${alpha(clusterColor, 0.2)}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <RouteIcon sx={{ fontSize: 15, color: clusterColor }} />
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: clusterColor,
                        letterSpacing: 0.5,
                      }}
                    >
                      ROUTE {clusterKey}
                    </Typography>
                    <Chip
                      label={
                        isMultiStopCluster
                          ? `${clusterItems.length} stops · shared route`
                          : '1 stop · solo'
                      }
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        bgcolor: alpha(clusterColor, 0.12),
                        color: clusterColor,
                        border: `1px solid ${alpha(clusterColor, 0.25)}`,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  </Box>
                )}
                {clusterItems.map((item) => {
                  const index = globalIndex++;
                  return (
                    <BulkBookingRowMobile
                      key={item.itemId}
                      item={item}
                      index={index}
                      primaryPickup={primaryPickup}
                      onUpdate={onUpdateItem}
                      onDelete={onDeleteItem}
                      onDuplicate={onDuplicateItem}
                      bulkOrder={bulkOrder}
                      showBulkBadge={showBulkBadge}
                    />
                  );
                })}
              </Box>
            );
          })}
        </Box>
      );
    }

    // Flat mobile layout (before pricing is calculated)
    return (
      <Box sx={{ p: 2 }}>
        {items.map((item, index) => (
          <BulkBookingRowMobile
            key={item.itemId}
            item={item}
            index={index}
            primaryPickup={primaryPickup}
            onUpdate={onUpdateItem}
            onDelete={onDeleteItem}
            onDuplicate={onDuplicateItem}
            bulkOrder={bulkOrder}
            showBulkBadge={showBulkBadge}
          />
        ))}
      </Box>
    );
  }

  // Desktop: Table layout (with optional cluster grouping)
  const tableHeaders = (
    <TableHead>
      <TableRow>
        <TableCell sx={{ ...HEADER_STYLE, width: 50 }}>{t('bulkBooking.headerHash')}</TableCell>
        <TableCell sx={{ ...HEADER_STYLE, minWidth: 200 }}>
          {t('bulkBooking.headerPickup')}
        </TableCell>
        <TableCell sx={{ ...HEADER_STYLE, minWidth: 250 }}>
          {t('bulkBooking.headerDelivery')}
        </TableCell>
        <TableCell sx={{ ...HEADER_STYLE, width: 100 }}>{t('bulkBooking.headerWeight')}</TableCell>
        <TableCell sx={{ ...HEADER_STYLE, width: 100 }}>{t('bulkBooking.headerVolume')}</TableCell>
        <TableCell sx={{ ...HEADER_STYLE, width: 120 }}>
          {t('bulkBooking.headerCategory')}
        </TableCell>
        <TableCell sx={{ ...HEADER_STYLE, width: 100 }} align="right">
          {t('bulkBooking.headerPrice')}
        </TableCell>
        <TableCell sx={{ ...HEADER_STYLE, width: 100 }} align="center">
          {t('bulkBooking.headerActions')}
        </TableCell>
      </TableRow>
    </TableHead>
  );

  if (hasClusterData && groupedItems) {
    // Render items grouped by cluster with cluster header rows
    let globalIndex = 0;
    return (
      <TableContainer sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Table size="small">
          {tableHeaders}
          <TableBody>
            {groupedItems.clusterOrder.map((clusterKey) => {
              const clusterItems = groupedItems.clusterMap[clusterKey];
              const isMultiStopCluster =
                clusterKey !== 'ungrouped' && clusterItems.some((it) => it.isMultiStop);
              return (
                <>
                  {clusterKey !== 'ungrouped' && (
                    <ClusterHeaderRow
                      key={`cluster-header-${clusterKey}`}
                      clusterId={clusterKey}
                      itemCount={clusterItems.length}
                      isMultiStop={isMultiStopCluster}
                      routeSavingsPercent={bulkOrder?.routeSavingsPercent}
                    />
                  )}
                  {clusterItems.map((item) => {
                    const index = globalIndex++;
                    return (
                      <BulkBookingRow
                        key={item.itemId}
                        item={item}
                        index={index}
                        primaryPickup={primaryPickup}
                        onUpdate={onUpdateItem}
                        onDelete={onDeleteItem}
                        onDuplicate={onDuplicateItem}
                        bulkOrder={bulkOrder}
                        showBulkBadge={showBulkBadge}
                      />
                    );
                  })}
                </>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Flat layout (no cluster data yet — before pricing is calculated)
  return (
    <TableContainer sx={{ maxWidth: 1200, mx: 'auto' }}>
      <Table size="small">
        {tableHeaders}
        <TableBody>
          {items.map((item, index) => (
            <BulkBookingRow
              key={item.itemId}
              item={item}
              index={index}
              primaryPickup={primaryPickup}
              onUpdate={onUpdateItem}
              onDelete={onDeleteItem}
              onDuplicate={onDuplicateItem}
              bulkOrder={bulkOrder}
              showBulkBadge={showBulkBadge}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default memo(BulkBookingTable);
